import React, { type FC, useEffect, useState } from 'react';
import { Box, TextButton } from '@wix/design-system';
import classNames from 'classnames';
import css from './UtilityComponents.scss';
import { useTranslation } from '@wix/wix-i18n-config';
import { DataHooks } from '../../constants/dataHooks';
import { useTemplatesPageBi } from '../../hooks/useTemplatesPageBi';

export const COLLAPSED_BOX_MIN_HEIGHT = 490;

type Props = {
  boxCollapsedHeight?: number;
  dataHook?: string;
  templateId: string;
};
export const CollapsibleTextBox: FC<Props> = ({
  children,
  boxCollapsedHeight = COLLAPSED_BOX_MIN_HEIGHT,
  dataHook,
  templateId,
}) => {
  const { t } = useTranslation();
  const [isTextExpanded, setIsTextExpanded] = useState<boolean>(false);
  const [showReadMore, setShowReadMore] = useState<boolean>(false);
  const textContainerRef = React.useRef<HTMLDivElement>(null);
  const textBoxWrapperRef = React.useRef<HTMLDivElement>(null);
  const { reportExpandCollapseClick } = useTemplatesPageBi();

  const onDetailsToggle = (element: HTMLDetailsElement) => {
    if (element.hasAttribute('open')) {
      setIsTextExpanded(true);
    }
  };

  const detailsListenerFunc = (event: Event) =>
    onDetailsToggle(event.target as HTMLDetailsElement);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height;
      setShowReadMore(height > boxCollapsedHeight);
    });
    textContainerRef.current &&
      resizeObserver.observe(textContainerRef.current);

    const detailsElements =
      textBoxWrapperRef.current?.querySelectorAll('details');
    detailsElements.forEach((element) => {
      element.addEventListener('toggle', detailsListenerFunc);
    });

    return () => {
      resizeObserver.disconnect();
      detailsElements.forEach((element) => {
        element.removeEventListener('toggle', detailsListenerFunc);
      });
    };
  }, [boxCollapsedHeight]);

  const onExpandCollapseClick = () => {
    reportExpandCollapseClick(templateId, isTextExpanded);
    if (isTextExpanded) {
      textBoxWrapperRef.current
        .querySelectorAll('details')
        .forEach((elem) => elem.removeAttribute('open'));
      textBoxWrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsTextExpanded(!isTextExpanded);
  };

  return (
    <div ref={textBoxWrapperRef} className={css.collapsibleTextBoxWrapper}>
      <Box
        dataHook={DataHooks.CollapsibleTextContainer}
        display="block"
        width="100%"
        maxHeight={isTextExpanded ? '100%' : `${boxCollapsedHeight}px`}
        overflow={isTextExpanded ? 'none' : 'hidden'}
        className={classNames({
          [css.collapsedTextBox]: showReadMore && !isTextExpanded,
        })}
      >
        <div data-hook={dataHook} ref={textContainerRef}>
          {children}
        </div>
      </Box>
      {showReadMore && (
        <TextButton
          dataHook={DataHooks.CollapsibleTextBoxReadMore}
          size="medium"
          className={css.readTextButton}
          onClick={onExpandCollapseClick}
        >
          {t(
            isTextExpanded
              ? 'app.collapsible-text-box.read-less'
              : 'app.collapsible-text-box.read-more',
          )}
        </TextButton>
      )}
    </div>
  );
};
